/**
 * LeadsListPage — full list of leads with filters, search, create, delete, bulk import.
 */

import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import {
  Button,
  Card,
  Table,
  SearchBar,
  Modal,
  Input,
  Dropdown,
} from '@/components/common';
import { useTenantDateFormatter } from '@/utils/datetime';
import { getFullName } from '@/utils';
import {
  useLeads,
  useCreateLead,
  useDeleteLead,
  getExportCsvUrl,
  type Lead,
  type LeadCreate,
} from '../api/leads';
import { BulkImportDialog } from '../components/BulkImportDialog';
import {
  STAGE_LABEL_MAP,
  STAGE_COLOR_MAP,
  INTEREST_LEVEL_LABEL_MAP,
  INTEREST_LEVEL_COLOR_MAP,
  SOURCE_LABEL_MAP,
  SOURCE_PRESETS,
  STAGE_PIPELINE_ORDER,
  type LeadStage,
  type InterestLevel,
} from '../config/ui';

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  width: 280px;
  @media (max-width: 768px) { width: 100%; }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const StageDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  margin-right: 6px;
`;

const StageChip = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}20;
`;

const InterestChip = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => $color}20;
`;

const ClickableName = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const SkeletonRow = styled.div`
  height: 52px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  animation: pulse 1.5s ease-in-out infinite;
  @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
`;

const ErrorBox = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
`;

const EmptyBox = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LoadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${({ theme }) => theme.spacing.md};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 300;

const STAGE_OPTIONS = [
  { value: '', label: 'All Stages' },
  ...STAGE_PIPELINE_ORDER.map((s) => ({
    value: s,
    label: STAGE_LABEL_MAP[s],
  })),
];

const INTEREST_OPTIONS = [
  { value: '', label: 'All Interest Levels' },
  { value: 'cold', label: 'Cold' },
  { value: 'warm', label: 'Warm' },
  { value: 'hot', label: 'Hot 🔥' },
];

const SOURCE_OPTIONS = [
  { value: '', label: 'Select source…' },
  ...SOURCE_PRESETS.map((s) => ({ value: s, label: SOURCE_LABEL_MAP[s] })),
];

export const LeadsListPage: React.FC = () => {
  const navigate = useNavigate();
  const formatDate = useTenantDateFormatter();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [interestFilter, setInterestFilter] = useState('');
  const [assignedFilter, setAssignedFilter] = useState('');

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  // Add lead form state
  const [form, setForm] = useState<LeadCreate>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: '',
    interests: [],
    notes: '',
    follow_up_date: '',
  });
  const [interestsText, setInterestsText] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(e.target.value), DEBOUNCE_MS);
  };

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useLeads({
    stage: stageFilter || undefined,
    interest_level: interestFilter || undefined,
    assigned_to: assignedFilter || undefined,
    search: debouncedSearch || undefined,
  });

  const createMutation = useCreateLead();
  const deleteMutation = useDeleteLead();

  const leads = data?.pages.flatMap((p) => p.items) ?? [];
  const total = data?.pages[0]?.total;

  const handleDelete = async (lead: Lead, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete lead ${lead.first_name} ${lead.last_name}?`)) return;
    await deleteMutation.mutateAsync(lead.id);
  };

  const handleAddSubmit = async () => {
    if (!form.first_name || !form.source) return;
    await createMutation.mutateAsync({
      ...form,
      interests: interestsText
        ? interestsText.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
    });
    setIsAddOpen(false);
    setForm({ first_name: '', last_name: '', email: '', phone: '', source: '', interests: [], notes: '', follow_up_date: '' });
    setInterestsText('');
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      isPrimary: true,
      render: (lead: Lead) => (
        <ClickableName onClick={() => navigate(`/leads/${lead.id}`)}>
          {getFullName(lead.first_name, lead.last_name)}
        </ClickableName>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (lead: Lead) => lead.phone || '—',
    },
    {
      key: 'stage',
      header: 'Stage',
      render: (lead: Lead) => {
        const color = STAGE_COLOR_MAP[lead.stage as LeadStage] ?? '#94a3b8';
        const label = STAGE_LABEL_MAP[lead.stage as LeadStage] ?? lead.stage;
        return <StageChip $color={color}><StageDot $color={color} />{label}</StageChip>;
      },
    },
    {
      key: 'interest_level',
      header: 'Interest',
      render: (lead: Lead) => {
        const color = INTEREST_LEVEL_COLOR_MAP[lead.interest_level as InterestLevel] ?? '#94a3b8';
        const label = INTEREST_LEVEL_LABEL_MAP[lead.interest_level as InterestLevel] ?? lead.interest_level;
        return <InterestChip $color={color}>{label}</InterestChip>;
      },
    },
    {
      key: 'follow_up_date',
      header: 'Follow-up',
      hideOnMobile: true,
      render: (lead: Lead) => lead.follow_up_date ? formatDate(lead.follow_up_date) : '—',
    },
    {
      key: 'source',
      header: 'Source',
      hideOnMobile: true,
      render: (lead: Lead) => SOURCE_LABEL_MAP[lead.source as keyof typeof SOURCE_LABEL_MAP] ?? lead.source,
    },
    {
      key: 'assigned_to',
      header: 'Assigned To',
      hideOnMobile: true,
      render: (lead: Lead) => lead.assigned_to || '—',
    },
    {
      key: 'actions',
      header: '',
      render: (lead: Lead) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => handleDelete(lead, e)}
        >
          Delete
        </Button>
      ),
    },
  ];

  const subtitle = typeof total === 'number' ? `${total} total leads` : `${leads.length} leads`;

  const exportParams: Record<string, string> = {};
  if (stageFilter) exportParams.stage = stageFilter;
  if (interestFilter) exportParams.interest_level = interestFilter;
  if (debouncedSearch) exportParams.search = debouncedSearch;

  return (
    <PageContainer
      title="Leads"
      subtitle={subtitle}
      data-testid="leads-list-page"
      data-flow="leads-page"
      actions={
        <HeaderActions>
          <Button
            variant="outline"
            data-testid="leads-import-btn"
            onClick={() => setIsImportOpen(true)}
          >
            Import
          </Button>
          <Button
            variant="outline"
            data-testid="leads-export-btn"
            onClick={() => window.open(getExportCsvUrl(exportParams), '_blank')}
          >
            Export CSV
          </Button>
          <Button onClick={() => setIsAddOpen(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Lead
          </Button>
        </HeaderActions>
      }
    >
      <Card padding="lg">
        <FiltersRow>
          <SearchWrapper>
            <SearchBar
              placeholder="Search leads…"
              value={searchQuery}
              onChange={handleSearchChange}
              data-testid="leads-search"
            />
          </SearchWrapper>
          <FilterGroup>
            <Dropdown
              testId="leads-filter-stage"
              options={STAGE_OPTIONS}
              value={stageFilter}
              onChange={setStageFilter}
              placeholder="All Stages"
            />
            <Dropdown
              testId="leads-filter-interest-level"
              options={INTEREST_OPTIONS}
              value={interestFilter}
              onChange={setInterestFilter}
              placeholder="All Interest Levels"
            />
            <Input
              placeholder="Assigned to…"
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e.target.value)}
            />
          </FilterGroup>
        </FiltersRow>

        {isLoading ? (
          <div data-testid="leads-loading">
            {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
          </div>
        ) : isError ? (
          <ErrorBox data-testid="leads-error-state">
            <p>{(error as Error)?.message ?? 'Failed to load leads.'}</p>
            <Button variant="outline" onClick={() => refetch()}>Retry</Button>
          </ErrorBox>
        ) : leads.length === 0 ? (
          <EmptyBox data-testid="leads-empty-state">
            <p>No leads found. Add your first lead to get started.</p>
          </EmptyBox>
        ) : (
          <>
            <Table
              data={leads}
              columns={columns}
              onRowClick={(lead) => navigate(`/leads/${lead.id}`)}
              emptyMessage="No leads found"
              testId="leads-table"
              getRowTestId={(l) => `lead-row-${l.id}`}
            />
            {hasNextPage && (
              <LoadMoreRow>
                <Button
                  variant="outline"
                  data-testid="leads-load-more"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              </LoadMoreRow>
            )}
          </>
        )}
      </Card>

      {/* Add Lead Modal */}
      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add New Lead"
        size="lg"
        data-testid="add-lead-form"
      >
        <form
          data-testid="add-lead-form"
          onSubmit={(e) => { e.preventDefault(); handleAddSubmit(); }}
        >
          <FormGrid>
            <Input
              label="First Name *"
              placeholder="First name"
              value={form.first_name}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              required
            />
            <Input
              label="Last Name"
              placeholder="Last name"
              value={form.last_name ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            />
            <Input
              label="Phone"
              type="tel"
              placeholder="+91 98765 43210"
              value={form.phone ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
            <Input
              label="Email"
              type="email"
              placeholder="lead@example.com"
              value={form.email ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <Dropdown
              label="Source *"
              options={SOURCE_OPTIONS}
              value={form.source}
              onChange={(v) => setForm((f) => ({ ...f, source: v }))}
              placeholder="Select source…"
            />
            <Input
              label="Interests (comma-separated)"
              placeholder="guitar, vocals"
              value={interestsText}
              onChange={(e) => setInterestsText(e.target.value)}
            />
            <Input
              label="Follow-up Date"
              type="date"
              value={form.follow_up_date ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, follow_up_date: e.target.value }))}
            />
          </FormGrid>
          <div style={{ marginTop: '16px' }}>
            <Input
              label="Notes"
              placeholder="Any notes about this lead…"
              value={form.notes ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          <ModalActions>
            <Button variant="outline" type="button" onClick={() => setIsAddOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="add-lead-submit"
              data-flow="lead-add-submit-btn"
              disabled={createMutation.isPending || !form.first_name || !form.source}
            >
              {createMutation.isPending ? 'Creating…' : 'Create Lead'}
            </Button>
          </ModalActions>
        </form>
      </Modal>

      <BulkImportDialog isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} />
    </PageContainer>
  );
};
